/** Shared quick-switcher page matching and sort rank (unit-tested). */

export type QuickNavPageForRank = {
  path: string;
  label: string;
  group: string;
  searchText?: string;
};

export function normalizeQuickNavPath(path: string): string {
  const trimmed = path.trim();
  if (trimmed.length <= 1) return trimmed;
  return trimmed.endsWith("/") ? trimmed.slice(0, -1) : trimmed;
}

/** Treat hyphens like spaces so "purchase invoice" matches `/mrp/purchase-invoices`. */
export function normalizeQuickSearchHay(value: string): string {
  return value
    .toLowerCase()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function isModuleRootQuickNavPage(
  page: QuickNavPageForRank,
  moduleRootPaths: ReadonlySet<string>,
): boolean {
  return moduleRootPaths.has(normalizeQuickNavPath(page.path));
}

export function pageMatchesQuery(page: QuickNavPageForRank, query: string): boolean {
  const q = normalizeQuickSearchHay(query);
  if (q.length === 0) return true;
  const hay = normalizeQuickSearchHay(`${page.label} ${page.path} ${page.group} ${page.searchText ?? ""}`);
  if (hay.includes(q)) return true;
  const tokens = q.split(" ").filter((token) => token.length > 0);
  return tokens.every((token) => hay.includes(token));
}

/**
 * Lower rank sorts earlier in Go to… page results.
 */
export function pageSearchRank(
  page: QuickNavPageForRank,
  trimmedQuery: string,
  moduleRootPaths: ReadonlySet<string>,
): number {
  const query = normalizeQuickSearchHay(trimmedQuery);
  if (query.length === 0) return 100;

  const labelLower = normalizeQuickSearchHay(page.label);
  const pathNorm = normalizeQuickSearchHay(normalizeQuickNavPath(page.path));
  const pathSegments = normalizeQuickNavPath(page.path)
    .toLowerCase()
    .split("/")
    .filter(Boolean);
  const lastSegment = normalizeQuickSearchHay(pathSegments[pathSegments.length - 1] ?? "");
  const extraLower = normalizeQuickSearchHay(page.searchText ?? "");
  const isModuleRoot = isModuleRootQuickNavPage(page, moduleRootPaths);

  if (labelLower === query) return 0;
  if (pathNorm === query && lastSegment === "settings") return 1;
  if (labelLower.startsWith(query)) return 5;
  if (lastSegment === query || lastSegment.startsWith(query)) return 8;
  if (labelLower.includes(query)) return 15;
  if (pathNorm.includes(query)) return 18;

  if (isModuleRoot) {
    const firstSegment = pathNorm.replace(/^\//, "").split("/")[0] ?? "";
    if (pathNorm === `/${query}` || firstSegment === query) return 0;
    if (extraLower.includes(query)) return 70;
    return 80;
  }

  if (extraLower.includes(query)) return 35;
  return 80;
}

/** Drop module home rows that only matched incidental `searchText` when a feature page is a closer fit. */
export function filterQuickNavPagesForQuery(
  pages: QuickNavPageForRank[],
  trimmedQuery: string,
  moduleRootPaths: ReadonlySet<string>,
): QuickNavPageForRank[] {
  const query = trimmedQuery.trim().toLowerCase();
  const matching = pages.filter((page) => pageMatchesQuery(page, query));
  if (query.length === 0) return matching;

  const hasStrongFeaturePage = matching.some((page) => {
    if (isModuleRootQuickNavPage(page, moduleRootPaths)) return false;
    const rank = pageSearchRank(page, query, moduleRootPaths);
    return rank < 35;
  });

  if (!hasStrongFeaturePage) return matching;

  return matching.filter((page) => {
    if (!isModuleRootQuickNavPage(page, moduleRootPaths)) return true;
    const pathNorm = normalizeQuickSearchHay(normalizeQuickNavPath(page.path));
    const firstSegment = normalizeQuickSearchHay(
      normalizeQuickNavPath(page.path).replace(/^\//, "").split("/")[0] ?? "",
    );
    if (pathNorm === query || firstSegment === query) return true;
    if (normalizeQuickSearchHay(page.label).includes(query)) return true;
    return false;
  });
}

export function compareQuickNavPages(
  left: QuickNavPageForRank,
  right: QuickNavPageForRank,
  trimmedQuery: string,
  moduleRootPaths: ReadonlySet<string>,
): number {
  const leftRank = pageSearchRank(left, trimmedQuery, moduleRootPaths);
  const rightRank = pageSearchRank(right, trimmedQuery, moduleRootPaths);
  if (leftRank !== rightRank) return leftRank - rightRank;
  const groupCompare = left.group.localeCompare(right.group, undefined, { sensitivity: "base" });
  if (groupCompare !== 0) return groupCompare;
  return left.label.localeCompare(right.label, undefined, { sensitivity: "base" });
}
